import Big from 'big.js';
import { positionBetween, truncatePosition } from 'js/utils/big';
import {
  compareCapabilities,
  parsePosition,
  sortCapabilities,
} from 'js/utils/capability-order';

export { compareCapabilities, sortCapabilities };

/** Maps each capability id to the capability, its parent id and its sorted siblings. */
export function buildCapabilityLookup(
  capabilities,
  collator,
  parentId = null,
  lookup = {}
) {
  const siblings = sortCapabilities(capabilities, collator);

  siblings.forEach((capability) => {
    lookup[capability.id] = { capability, parentId, siblings };
    buildCapabilityLookup(capability.children, collator, capability.id, lookup);
  });

  return lookup;
}

export function evenlySpacedPositions(ids) {
  return Object.fromEntries(
    ids.map((id, index) => [
      id,
      truncatePosition(
        Big(index + 1)
          .div(ids.length + 1)
          .toString()
      ),
    ])
  );
}

/** A position that sorts after every positioned capability in `siblings`. */
export function positionAfter(siblings) {
  const last = (siblings ?? [])
    .map((s) => parsePosition(s.position))
    .filter(Boolean)
    .reduce((max, p) => (max === null || p.gt(max) ? p : max), null);

  return positionBetween(last ?? 0, 1);
}

/**
 * Positions needed to place `movedId` at `index` among `siblings` (index
 * counts the siblings other than the moved capability). Usually that is a
 * single new position for the moved capability; when its neighbors have no
 * room between them, every sibling is respaced.
 */
export function positionsForInsert(siblings, movedId, index) {
  const others = (siblings ?? []).filter((s) => s.id !== movedId);
  const prev = others[index - 1];
  const next = others[index];
  const prevPosition = prev ? parsePosition(prev.position) : Big(0);
  const nextPosition = next ? parsePosition(next.position) : Big(1);

  if (prevPosition && nextPosition && prevPosition.lt(nextPosition)) {
    const position = positionBetween(prevPosition, nextPosition);
    const bigPosition = Big(position);

    if (bigPosition.gt(prevPosition) && bigPosition.lt(nextPosition)) {
      return { [movedId]: position };
    }
  }

  const orderedIds = others.map((s) => s.id);
  orderedIds.splice(index, 0, movedId);

  return evenlySpacedPositions(orderedIds);
}

export function alphabeticalPositions(capabilities, collator) {
  const sorted = [...(capabilities ?? [])].sort(
    (a, b) => collator.compare(a.name, b.name) || collator.compare(a.id, b.id)
  );

  return evenlySpacedPositions(sorted.map((c) => c.id));
}

function isSelfOrDescendant(lookup, capabilityId, ancestorId) {
  let id = capabilityId;

  while (id != null) {
    if (id === ancestorId) {
      return true;
    }
    id = lookup[id]?.parentId;
  }

  return false;
}

function resolveSiblingMove(lookup, sourceId, siblingId, offset) {
  const entry = lookup[siblingId];

  if (!entry) {
    return null;
  }

  const others = entry.siblings.filter((s) => s.id !== sourceId);
  const index = others.findIndex((s) => s.id === siblingId) + offset;

  return { parentId: entry.parentId, siblings: entry.siblings, index };
}

/**
 * Works out where a dragged capability lands for a tree-item drop
 * `instruction` on `targetId` (null for the capability set itself, which
 * appends to `rootCapabilities`). Returns the new parent id and the positions
 * to save, or null when the drop is not allowed.
 */
export function resolveCapabilityMove({
  lookup,
  rootCapabilities,
  collator,
  sourceId,
  targetId,
  instruction,
}) {
  let move = null;

  if (targetId == null) {
    const siblings = sortCapabilities(rootCapabilities, collator);
    move = {
      parentId: null,
      siblings,
      index: siblings.filter((s) => s.id !== sourceId).length,
    };
  } else {
    switch (instruction?.type) {
      case 'reorder-above':
        move = resolveSiblingMove(lookup, sourceId, targetId, 0);
        break;

      case 'reorder-below':
        move = resolveSiblingMove(lookup, sourceId, targetId, 1);
        break;

      case 'make-child': {
        const siblings = sortCapabilities(
          lookup[targetId]?.capability.children,
          collator
        );
        move = {
          parentId: targetId,
          siblings,
          index: siblings.filter((s) => s.id !== sourceId).length,
        };
        break;
      }

      case 'reparent': {
        let ancestorId = targetId;
        for (
          let level = instruction.currentLevel;
          level > instruction.desiredLevel && ancestorId != null;
          level--
        ) {
          ancestorId = lookup[ancestorId]?.parentId;
        }
        move =
          ancestorId == null
            ? null
            : resolveSiblingMove(lookup, sourceId, ancestorId, 1);
        break;
      }

      default:
        move = null;
    }
  }

  if (!move || isSelfOrDescendant(lookup, move.parentId, sourceId)) {
    return null;
  }

  return {
    parentId: move.parentId,
    positions: positionsForInsert(move.siblings, sourceId, move.index),
  };
}
